import React, { useState } from 'react';
import { Property } from '../types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../components/InfoTooltip';
export function PropertyInformation() {
  // Mock data for a Thailand resort
  const [property, setProperty] = useState<Property>({
    name: 'Siam Bay Tropical Paradise Resort',
    description: 'Nestled on the pristine shores of Koh Samui, Siam Bay Tropical Paradise Resort offers a perfect blend of Thai luxury and natural beauty. Surrounded by lush tropical gardens and overlooking the crystal-clear waters of the Gulf of Thailand, this exclusive retreat provides an authentic Thai experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Beach Access', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Thai Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: '124 Moo 3, Chaweng Beach Road',
      city: 'Koh Samui',
      postalCode: '84320',
      country: 'Thailand',
      latitude: '9.5120',
      longitude: '100.0136'
    },
    contact: {
      email: 'reservations@siambaytropical.com',
      phone: '+66 77 123 456',
      website: 'https://www.siambaytropicalresort.com',
      timezone: 'Asia/Bangkok'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Thai Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Thai Massage', 'Muay Thai Classes', 'Longtail Boat Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-4971">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-4972">
            {tag}
            <button className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-4973">
              <XIcon size={12} data-id="element-4974" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-4975">
      {/* Simplified header section */}
      <div data-id="element-4976">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-4977">Hotel</h1>
        <div className="flex justify-between items-center" data-id="element-4978">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-4979">
            <div className="flex space-x-6" data-id="element-4980">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-4981">
                Hotel Information
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-4982">
                Media assets
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-4983">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-4984">
              <span className="mr-2" data-id="element-4985">Draft: Submitted for review</span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-4986" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-4987">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-4988">
                Draft Status
              </h3>
              <p className="text-xs text-gray-600" data-id="element-4989">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-4990">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4991">
          <div data-id="element-4992">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4993">
              Hotel name
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-4994" />
          </div>
          <div data-id="element-4995">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4996">
              Designation
            </label>
            <div className="relative" data-id="element-4997">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-4998">
                <option value="Resort" data-id="element-4999">Resort</option>
                <option value="Hotel" data-id="element-5000">Hotel</option>
                <option value="Villa" data-id="element-5001">Villa</option>
                <option value="Guesthouse" data-id="element-5002">Guesthouse</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-5003">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-5004" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-5005">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5006">
            Description
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-5007" />
        </div>
        <div className="mb-6" data-id="element-5008">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5009">
            Star rating
          </label>
          <div className="relative" data-id="element-5010">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-5011">
              <option value="5" data-id="element-5012">5</option>
              <option value="4" data-id="element-5013">4</option>
              <option value="3" data-id="element-5014">3</option>
              <option value="2" data-id="element-5015">2</option>
              <option value="1" data-id="element-5016">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-5017">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-5018" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-5019">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5020">
            Tags
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5021">
          <div data-id="element-5022">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5023">
              Address
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-5024" />
          </div>
          <div data-id="element-5025">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5026">
              Area name
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Chaweng Beach" data-id="element-5027" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5028">
          <div data-id="element-5029">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5030">
              City
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-5031" />
          </div>
          <div data-id="element-5032">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5033">
              Postal code
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-5034" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-5035">
          <div data-id="element-5036">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5037">
              Country
            </label>
            <div className="relative" data-id="element-5038">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-5039">
                <option value="Thailand" data-id="element-5040">Thailand</option>
                <option value="Malaysia" data-id="element-5041">Malaysia</option>
                <option value="Indonesia" data-id="element-5042">Indonesia</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-5043">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-5044" />
              </div>
            </div>
          </div>
          <div data-id="element-5045">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5046">
              Latitude
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-5047" />
          </div>
          <div data-id="element-5048">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5049">
              Longitude
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-5050" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5051">
          <div data-id="element-5052">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5053">
              Contact Email
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-5054" />
          </div>
          <div data-id="element-5055">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5056">
              Phone number
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-5057" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5058">
          <div data-id="element-5059">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5060">
              Website
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-5061" />
          </div>
          <div data-id="element-5062">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5063">
              Timezone
            </label>
            <div className="relative" data-id="element-5064">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-5065">
                <option value="Asia/Bangkok" data-id="element-5066">Asia/Bangkok</option>
                <option value="Asia/Kuala_Lumpur" data-id="element-5067">Asia/Kuala_Lumpur</option>
                <option value="Asia/Singapore" data-id="element-5068">Asia/Singapore</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-5069">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-5070" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5071">
          <div data-id="element-5072">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5073">
              Check-in Time
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-5074" />
          </div>
          <div data-id="element-5075">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5076">
              Check-out Time
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-5077" />
          </div>
        </div>
        <div className="mb-6" data-id="element-5078">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5079">
            Hotel Amenities
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-5080">
            <div className="flex flex-wrap gap-2" data-id="element-5081">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-5082">
                  {amenity}
                  <button className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-5083">
                    <XIcon size={12} data-id="element-5084" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-5085">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5086">
            Transportation
          </label>
          <div className="space-y-2" data-id="element-5087">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-5088">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5089" />
                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-5090">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-5091">
      <polyline points="6 9 12 15 18 9" data-id="element-5092"></polyline>
    </svg>;
}